import { Avatar, notification } from 'antd';
import { onMessage } from 'firebase/messaging';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { messaging } from '@/lib/firebase/get-firebase-messaging-instance';
import { encryptUrl } from '@shared/hooks/use-encrypted-params';
import { NOTIFICATION_REDIRECTION_KEY } from '@shared/constants';
import { useMarkSingleRead } from '@modules/notifications/hooks/use-mark-notification-read';
import { useMarkSingleAsSeen } from '@modules/notifications/hooks/use-mark-notification-seen';
import { ALL_MEMBERS_ROUTE } from '@modules/conversations/features/all-members-conversation/all-members-conversation.constants';

export function useForegroundNotification() {
  const params = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { markSingleReadHandler } = useMarkSingleRead(false);
  const { markAsSeenNotification } = useMarkSingleAsSeen();

  if (!messaging) return;
  onMessage(messaging, (payload) => {
    const {
      redirectUrl,
      isExternal,
      notificationImageUrl,
      notificationTitle,
      notificationBody,
      notificationId,
      clientId,
      channelId,
      redirection,
      senderId,
    } = payload?.data || {};

    const isExternalLink = isExternal === 'true';
    const hasRedirectionLink = redirectUrl && redirectUrl !== 'undefined';
    // in case of member call notification, channelId doesnot exist
    const isConversationOpen =
      channelId &&
      pathname.includes('/conversations') &&
      channelId === params?.channelId &&
      clientId === params?.clientId;

    const isMemberConversationOpen = senderId === params?.memberId;

    // If the conversation is currently open and a notification ID exists,
    if (isConversationOpen && notificationId) {
      markSingleReadHandler(+notificationId, true);
      markAsSeenNotification({ variables: { notificationId: +notificationId } });
      return;
    }

    if (notificationId && isMemberConversationOpen) {
      markSingleReadHandler(+notificationId, true);
      markAsSeenNotification({ variables: { notificationId: +notificationId } });
      return;
    }

    notification.info({
      icon: notificationImageUrl ? <Avatar size={32} src={notificationImageUrl} /> : null,
      className: 'hover:bg-gray-100 cursor-pointer',
      message: notificationTitle,
      description: notificationBody,
      onClick: handleClickToast,
      key: notificationId,
    });

    function handleClickToast() {
      notification.destroy(notificationId);

      //mark notification as read and seen on click
      if (notificationId) {
        markSingleReadHandler(+notificationId, true);
        markAsSeenNotification({ variables: { notificationId: +notificationId } });
      }

      //If there is no redirection link, navigate to the AI dashboard
      if (!hasRedirectionLink) {
        return navigate('/analytics/ai-dashboard', { state: { notification: true } });
      }

      //If the link is external, open it in a new browser tab/window
      if (isExternalLink) {
        return window.open(redirectUrl);
      }

      //if redirection exist and is chat box link, navigate to the specific conversation.
      if (redirection) {
        const parsedRedirection = JSON.parse(redirection);
        const { key, params } = parsedRedirection;
        if (key === NOTIFICATION_REDIRECTION_KEY.CHAT_BOX) {
          const redirectUrl = `/conversations/${params?.channelId}/${params?.clientId}`;
          navigate(redirectUrl);
          return;
        }
        if (key === NOTIFICATION_REDIRECTION_KEY.ALL_MEMBERS_CHAT) {
          navigate(ALL_MEMBERS_ROUTE);
          return;
        }

        if (key === NOTIFICATION_REDIRECTION_KEY.MEMBER_CHAT) {
          const redirectUrl = `/conversations/member/${params?.memberId}`;
          navigate(redirectUrl);
          return;
        }
      }
      const [rootUrl, queryParams] = redirectUrl.split('?');

      if (queryParams) {
        navigate(`${rootUrl}?${encryptUrl(queryParams)}`);
      } else {
        navigate(rootUrl);
      }

      notification.destroy();
    }
  });
}
